import axios from "axios";

// Generic, stateless HTTP helpers used throughout ContextApi.js and exposed
// to consumers via useContextStateManagement(). None of these close over
// component state - they only take token/url/body as arguments - so they
// live outside the provider as plain, stable functions.

export const getApiErrorMessage = (error) =>
  error?.response?.data?.error ||
  error?.message ||
  "An error occurred while processing your request";

export const usersPostRequest = async (token, apiUrl, obj) => {
  try {
    if (token) {
      const response = await axios.post(apiUrl, obj, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    }
  } catch (error) {
    return error.response.data.error;
  }
};

export const updateRequest = async (token, apiUrl, obj) => {
  try {
    if (token) {
      const response = await axios.put(apiUrl, obj, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    }
  } catch (error) {
    return error.response.data.error;
  }
};

export const patchRequest = async (token, apiUrl, obj) => {
  try {
    if (token) {
      const response = await axios.patch(apiUrl, obj, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    }
  } catch (error) {
    return error.response?.data || error.response?.data?.error || "";
  }
};

export const dateRangeRequest = async (token, apiUrl, obj) => {
  try {
    if (token) {
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: obj,
      });
      return response.data;
    }
    return "";
  } catch (error) {
    return error.response ? error.response.data.error : "";
  }
};

// paginatedRequest is dateRangeRequest's twin for endpoints that opt into
// server-side pagination (Inbound, Outbound, Summary). It surfaces the
// X-Total-Count / X-Has-More / X-Next-Offset response headers alongside
// the body so list views can implement Load-More.
//
// The body shape from those endpoints is unchanged from before pagination
// landed (still a bare array, or "no record"); only headers tell us
// whether more pages exist.
export const paginatedRequest = async (token, apiUrl, params = {}) => {
  if (!token) {
    return { rows: [], total: 0, hasMore: false, nextOffset: 0, error: null };
  }
  try {
    const response = await axios.get(apiUrl, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    const headers = response.headers || {};
    const data = response.data;
    const rows = Array.isArray(data) ? data : [];
    const total = Number.parseInt(headers["x-total-count"], 10);
    const hasMore =
      String(headers["x-has-more"] ?? "").toLowerCase() === "true";
    const nextOffset = Number.parseInt(headers["x-next-offset"], 10);
    return {
      rows,
      total: Number.isFinite(total) ? total : rows.length,
      hasMore,
      nextOffset: Number.isFinite(nextOffset) ? nextOffset : rows.length,
      practiceTimezone: headers["x-practice-timezone"] || null,
      // The practice's own configured zone (GeneralCallSettings.SelectedTimezone, falling back
      // to a real default server-side even when that table has no row for this practice) -
      // distinct from practiceTimezone above, which is the viewer's own effective zone. Used to
      // show a call's practice-local time alongside the viewer's own in CallsBreakdownDialog.
      fixedTimezone: headers["x-fixed-timezone"] || null,
      error: null,
    };
  } catch (error) {
    return {
      rows: [],
      total: 0,
      hasMore: false,
      nextOffset: 0,
      practiceTimezone: null,
      fixedTimezone: null,
      error: error.response ? error.response.data?.error || error.message : error.message,
    };
  }
};

export const fetchUserRecord = async (token, apiUrl) => {
  try {
    if (token) {
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    }
    return "";
  } catch (error) {
    return error.response ? (error.response.data?.error ?? "") : "";
  }
};

export const deleteRequest = async (token, apiUrl, obj) => {
  try {
    if (token) {
      const response = await axios.delete(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: obj,
      });
      return response.data;
    }
  } catch (error) {
    return error.response ? error.response.data.error : "";
  }
};

export const authorizedGetRequest = async (token, apiUrl, params = {}) => {
  if (!token) {
    throw new Error("User token is missing");
  }

  try {
    const response = await axios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const postRequest = async (token, apiUrl, obj) => {
  if (!token) {
    throw new Error("User token is missing");
  }

  try {
    const response = await axios.post(apiUrl, obj, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const authorizedPutRequest = async (token, apiUrl, obj) => {
  if (!token) {
    throw new Error("User token is missing");
  }

  try {
    const response = await axios.put(apiUrl, obj, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const multipartPostRequest = async (token, apiUrl, formData) => {
  if (!token) {
    throw new Error("User token is missing");
  }

  try {
    const response = await axios.post(apiUrl, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};
